import React, { useContext, useEffect, useState } from 'react';
import { Link, FormControlLabel } from '@material-ui/core';
import { Context as AuthContext } from '../../../contexts/authContext';
import { TwoFactorFormVerification } from './TwoFactorFormVerification';
import { update2FAMethod, generate2FA } from '../../../services/authService';
import useSnackbar from '../../../hooks/useSnackbar';
import { Severity } from '../../../types';
import { logger } from '../../../services/logger';
import { MDButton } from '../../MDButton';
import { MDBox } from '../../MDBox';
import { MDTypography } from '../../MDTypography';
import { Radio, RadioGroup } from '@mui/material';

export const TwoFactorAuth = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const [twoFactorData, setTwoFactorData] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('sms');
  const [user2FaMethod, setUser2FaMethod] = useState('');
  const toast = useSnackbar();

  useEffect(() => {
    if (user?.twoFactorEnabled && user?.twoFactorMethod) {
      setSelectedMethod(user?.twoFactorMethod);
    }
    if (user?.twoFactorMethod) {
      setUser2FaMethod(user?.twoFactorMethod);
    }
  }, [user]);

  const onEnable2FA = async () => {
    setIsLoading(true);
    const qr = await generate2FA();
    setTwoFactorData(qr);
    setIsLoading(false);
  };

  const on2FASuccess = async () => {
    try {
      await update2FAMethod(selectedMethod);
      if (refreshUser) {
        await refreshUser();
      }
      setTwoFactorData('');
      toast(
        '2-Factor Authentication method changed successfully',
        Severity.success
      );
    } catch (error) {
      logger.error('Failed to update 2FA method', { error: error as Error });
      toast('Unknown error', Severity.error);
    }
    setIsLoading(false);
  };

  const on2FASelectionChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedMethod(event.target.value);
  };

  const onSaveHandler = async () => {
    try {
      await update2FAMethod(selectedMethod);
      toast(`2FA method changed to ${selectedMethod}`, Severity.success);
    } catch (error) {
      toast(`Error: ${(error as any).message}`, Severity.error);
    }
  };

  return (
    <MDBox
      padding="20px"
      border="1px solid #ddd"
      borderRadius="8px"
      boxShadow="0 2px 4px rgba(0,0,0,0.1)"
    >
      <MDTypography variant="h5" gutterBottom>
        Enhance Your Security with 2-Factor Authentication
      </MDTypography>
      <MDTypography variant="body1" paragraph>
        Enable 2-factor authentication to add an extra layer of security to your
        account.
      </MDTypography>
      <MDTypography variant="body1" paragraph>
        We recommend using one of the following authenticator apps or methods:
      </MDTypography>
      <RadioGroup value={selectedMethod} onChange={on2FASelectionChange}>
        <FormControlLabel value="sms" control={<Radio />} label="SMS" />
        <FormControlLabel value="email" control={<Radio />} label="Email" />
        <FormControlLabel
          value="authenticator"
          control={<Radio />}
          label="Authenticator App"
        />
      </RadioGroup>
      {selectedMethod !== 'authenticator' && (
        <MDButton
          variant="gradient"
          color="info"
          onClick={onSaveHandler}
          disabled={
            isLoading ||
            selectedMethod === 'authenticator' ||
            selectedMethod === user2FaMethod
          }
        >
          Save
        </MDButton>
      )}
      {selectedMethod === 'authenticator' && (
        <>
          <MDBox
            display="flex"
            alignItems="center"
            marginBottom="20px"
            marginTop="20px"
          >
            <Link
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              target="_blank"
              rel="noopener"
              style={{
                display: 'flex',
                alignItems: 'center',
                marginRight: '20px',
              }}
            >
              <img
                src="/google-authenticator-2.svg"
                width={40}
                style={{ marginRight: '8px' }}
              />
              Google Authenticator
            </Link>
            <Link
              href="https://www.microsoft.com/en-us/account/authenticator"
              target="_blank"
              rel="noopener"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <img
                src="/microsoft-authenticator.svg"
                width={40}
                style={{ marginRight: '8px' }}
              />
              Microsoft Authenticator
            </Link>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            onClick={onEnable2FA}
            disabled={isLoading || twoFactorData !== ''}
          >
            Scan QR
          </MDButton>
          {twoFactorData && (
            <div style={{ textAlign: 'center', marginTop: 40 }}>
              <MDTypography>
                Scan the QR code on your authentication app
              </MDTypography>
              <img src={twoFactorData} alt="2FA_QR" />
              <MDTypography style={{ marginBottom: 30 }}>
                Once done scanning please verify
              </MDTypography>
              <TwoFactorFormVerification onVerifySuccess={on2FASuccess} />
            </div>
          )}
        </>
      )}
    </MDBox>
  );
};
